import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import userSignaturesService from "../../../api/userSignatures";

const UploadSignatureField = ({ onFileDrop }) => {
  const [fileInfo, setFileInfo] = useState(null);
  const fileInputRef = useRef();
  const user = JSON.parse(localStorage.getItem("user"));

  const fetchUploadedFile = async () => {
    if (!user?.userId) return;

    try {
      const res = await userSignaturesService.getUploadedFile(user.userId);

      const mimeType = res.headers["content-type"];
      const contentDisposition = res.headers["content-disposition"];
      let filename = "Uploaded File";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match && match[1]) filename = match[1];
      }

      let type = "other";
      if (mimeType.startsWith("image/")) type = "image";
      else if (
        mimeType.startsWith("text/") ||
        filename.endsWith(".json") ||
        filename.endsWith(".csv")
      )
        type = "text";
      else if (mimeType === "application/pdf") type = "pdf";

      if (type === "text") {
        const text = await res.data.text();
        setFileInfo({ type: "text", name: filename, content: text });
      } else {
        const url = URL.createObjectURL(
          new Blob([res.data], { type: mimeType }),
        );
        setFileInfo({ type, name: filename, content: url });
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error("Error fetching uploaded file.");
      }
    }
  };

  useEffect(() => {
    fetchUploadedFile();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = file.type;
    const reader = new FileReader();

    if (fileType.startsWith("image/")) {
      reader.onload = (event) =>
        setFileInfo({
          type: "image",
          name: file.name,
          content: event.target.result,
        });
      reader.readAsDataURL(file);
    } else if (
      fileType.startsWith("text/") ||
      file.name.endsWith(".json") ||
      file.name.endsWith(".csv")
    ) {
      reader.onload = (event) =>
        setFileInfo({
          type: "text",
          name: file.name,
          content: event.target.result,
        });
      reader.readAsText(file);
    } else if (fileType === "application/pdf") {
      const url = URL.createObjectURL(file);
      setFileInfo({ type: "pdf", name: file.name, content: url });
    } else {
      setFileInfo({ type: "other", name: file.name, content: null });
    }

    handleUploadToDB(file);
  };

  const handleUploadToDB = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", user?.userId);

    try {
      await userSignaturesService.saveUploadSignature(formData);
      toast.success("File uploaded successfully!");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info(error.response.data);
      } else {
        toast.error("Failed to save signature.");
      }
    }
  };

  const handleDelete = async () => {
    try {
      await userSignaturesService.softDeleteSignature(user?.userId, "UPLOAD");
      setFileInfo(null); // remove preview
      toast.success("Signature deleted successfully!");
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast.error("Failed to delete signature.");
    }
  };

  const handleDragStart = (e) => {
    if (fileInfo) {
      e.dataTransfer.setData("text/plain", fileInfo.content);
      e.dataTransfer.setData("fieldType", fileInfo.type);
    }
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        alignItems: "center",
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ marginBottom: "10px" }}
      />

      {fileInfo && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            width: "100%",
            maxWidth: "600px",
          }}
        >
          {/* File preview */}
          <div
            style={{
              flex: 1,
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              maxHeight: "150px",
              overflow: "auto",
            }}
            draggable
            onDragStart={handleDragStart}
          >
            {fileInfo.type === "image" && (
              <img
                src={fileInfo.content}
                alt="Uploaded preview"
                style={{ width: "100px", height: "auto" }}
              />
            )}
            {fileInfo.type === "text" && (
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  maxHeight: "100px",
                  overflowY: "auto",
                }}
              >
                {fileInfo.content}
              </pre>
            )}
            {fileInfo.type === "pdf" && (
              <iframe
                src={fileInfo.content}
                title="PDF Preview"
                width="100px"
                height="100px"
                style={{ border: "none" }}
              />
            )}
            {fileInfo.type === "other" && (
              <p style={{ color: "#666" }}>Preview not supported</p>
            )}
          </div>

          {/* Delete button outside preview */}
          <Button
            variant="danger"
            onClick={handleDelete}
            style={{ height: "40px", flexShrink: 0 }}
          >
            <FaTrash />
          </Button>
        </div>
      )}

      {!fileInfo && (
        <div style={{ fontSize: "12px", color: "#888" }}>
          Upload any file to preview it here.
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
      />
    </div>
  );
};

export default UploadSignatureField;
